import api from '@/api/api';
import { useConfig } from '@/hooks/useConfig';
import { useDialogs } from '@/hooks/useDialogs';
import { useMetadata } from '@/hooks/useMetadata';
import { Application } from '@/types/application';
import { BackendApplication } from '@/types/backend';
import { handleFileChange } from '@/util/file';
import { migrateApplication } from '@/util/migration/migration';
import { formatDate } from '@/util/time';
import { isApplication } from '@/util/typeGuards';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Button, Chip, CircularProgress, Typography } from '@mui/material';
import { Box, Stack, useTheme } from '@mui/system';
import { ChangeEvent, useRef, useState, type FC } from 'react';
import { useAuth } from 'react-oidc-context';
import { Side } from '../BlockEngineComparer';
import { getAppLabel } from '../comparerUtils/compareApplications';
import ApplicationPickerDialog from './ApplicationPickerDialog';

type ApplicationPickerProps = {
  side: Side;
  label: string;
  isSmallScreen: boolean;
  selectedAppId: string | undefined;
  onAppSelected: (side: Side, newApp?: Application | undefined, label?: string) => void;
};

const ApplicationPicker: FC<ApplicationPickerProps> = ({
  side,
  label,
  isSmallScreen,
  selectedAppId,
  onAppSelected
}) => {
  const { config } = useConfig();
  const auth = useAuth();
  const { showError, showTable } = useDialogs();
  const { metadata } = useMetadata();
  const theme = useTheme();

  const fileRef = useRef<HTMLInputElement>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSelectedFile = (event: ChangeEvent<HTMLInputElement>) => {
    handleFileChange(
      event,
      (app) => {
        setIsLoading(false);

        if (app) {
          const migratedApp = migrateApplication(app, metadata, showTable);
          onAppSelected(side, migratedApp, getAppLabel(migratedApp));
        }
      },
      (error) => {
        showError({
          title: 'Could not load the application file',
          message: error.message,
          stacktrace: error
        });
      }
    );
  };

  const handleSelectedApplication = (backendApp: BackendApplication, revision: number) => {
    if (!backendApp.applicationId) return;
    setOpenDialog(false);
    setIsLoading(true);
    api(config.apiUrl, auth.user?.access_token ?? '')
      .application.load(backendApp.applicationId, revision)
      .then((revision) => {
        if (!revision) throw new Error('Empty response');
        const app: Application = JSON.parse(revision.blockData);
        if (!isApplication(app)) throw new Error('The retrieved object is not a valid application');
        const migratedApp = migrateApplication(app, metadata, showTable);
        const name = backendApp.label ?? backendApp.name ?? backendApp.applicationId;
        const label = `${name} (rev ${revision.revision}, ${formatDate(revision.created)})`;
        onAppSelected(side, migratedApp, label);
      })
      .catch((error) =>
        showError({
          title: 'Could not load the application',
          message: error.message,
          stacktrace: error
        })
      )
      .finally(() => setIsLoading(false));
  };

  return (
    <Box flex={1} border={1} borderColor='divider' borderRadius={2}>
      <Box
        display='flex'
        flexDirection={isSmallScreen ? 'column' : 'row'}
        justifyContent='space-between'
        alignItems={isSmallScreen ? 'flex-start' : 'center'}
        px={2}
        py={1}
        bgcolor={theme.palette.action.hover}
      >
        <Typography>Version {side}</Typography>
        {label && (
          <Chip
            label={label}
            size='small'
            color={side === 'A' ? 'error' : 'success'}
            variant='outlined'
            onDelete={() => onAppSelected(side)}
          />
        )}
      </Box>
      <Box p={2}>
        <Stack direction='row'>
          <Button variant='outlined' startIcon={<FolderOpenIcon />} onClick={() => setOpenDialog(true)} sx={{ mr: 1 }}>
            Browse…
          </Button>
          <Button variant='outlined' startIcon={<UploadFileIcon />} onClick={() => fileRef.current?.click()}>
            Upload file
          </Button>
        </Stack>
        <input ref={fileRef} hidden accept='.json,application/json' type='file' onChange={handleSelectedFile} />

        {isLoading && (
          <Box>
            <CircularProgress />
          </Box>
        )}
      </Box>

      <ApplicationPickerDialog
        open={openDialog}
        title={`Select version ${side}`}
        onClose={() => setOpenDialog(false)}
        selectedAppId={selectedAppId}
        onSelect={handleSelectedApplication}
      />
    </Box>
  );
};

export default ApplicationPicker;
